import React from 'react';
import { X, CheckCircle, Clock, Car, CreditCard, QrCode, Banknote, ExternalLink } from 'lucide-react';

const ReceiptModal = ({ isOpen, onClose, receiptData }) => {
  if (!isOpen || !receiptData) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr)?.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'card': return <CreditCard className="w-4 h-4" />;
      case 'qr': return <QrCode className="w-4 h-4" />;
      case 'cash': return <Banknote className="w-4 h-4" />;
      default: return <CreditCard className="w-4 h-4" />;
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case 'card': return 'Credit / Debit Card';
      case 'qr': return 'QR Code Payment';
      case 'cash': return 'Cash';
      default: return method || 'Card';
    }
  };

  const receiptNumber = receiptData?.transactionId
    ? `#${receiptData?.transactionId?.slice(0, 8)?.toUpperCase()}`
    : `#${Date.now()?.toString(36)?.toUpperCase()}`;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full relative animate-fadeIn max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Booking Receipt</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Success Badge */}
          <div className="flex flex-col items-center py-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Booking Confirmed</h3>
            <p className="text-sm text-gray-500 mt-1">Receipt {receiptNumber}</p>
            <p className="text-xs text-gray-400 mt-0.5">{formatDate(receiptData?.createdAt || new Date()?.toISOString())}</p>
          </div>

          {/* Route Details */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex-shrink-0">
                <div className="w-3 h-3 rounded-full bg-green-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500">Pickup</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{receiptData?.from || 'N/A'}</p>
              </div>
            </div>
            <div className="ml-1.5 w-px h-4 bg-gray-300" />
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex-shrink-0">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500">Destination</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{receiptData?.to || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Trip Info */}
          <div className="grid grid-cols-2 gap-3">
            {receiptData?.serviceType && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Car className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-xs text-gray-500">Service</span>
                </div>
                <p className="text-sm font-semibold text-gray-900">{receiptData?.serviceType}</p>
              </div>
            )}
            {receiptData?.scheduledTime && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Clock className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-xs text-gray-500">Time</span>
                </div>
                <p className="text-sm font-semibold text-gray-900">{receiptData?.scheduledTime}</p>
              </div>
            )}
          </div>

          {/* Payment Details */}
          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Payment Details</p>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-600">
                  {getPaymentMethodIcon(receiptData?.paymentMethod)}
                  <span className="text-sm">{getPaymentMethodLabel(receiptData?.paymentMethod)}</span>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  receiptData?.paymentStatus === 'succeeded' ?'bg-green-100 text-green-700'
                    : receiptData?.paymentStatus === 'pending' ?'bg-yellow-100 text-yellow-700' :'bg-gray-100 text-gray-600'
                }`}>
                  {receiptData?.paymentStatus === 'succeeded' ? 'Paid' :
                   receiptData?.paymentStatus === 'pending'? 'Pending' : receiptData?.paymentStatus ||'Confirmed'}
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <span className="text-base font-bold text-gray-900">Total Paid</span>
                <span className="text-xl font-bold text-blue-600">{receiptData?.amount} THB</span>
              </div>
            </div>
          </div>

          {/* Stripe Receipt Link */}
          {receiptData?.stripeReceiptUrl && (
            <a
              href={receiptData?.stripeReceiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 border-2 border-blue-200 text-blue-600 hover:bg-blue-50 font-semibold rounded-xl transition-colors text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              View Official Stripe Receipt
            </a>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl transition-all shadow-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReceiptModal;
